import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Plus, Search, Edit, Trash2, Eye, EyeOff, FileText,
  Calendar, Image as ImageIcon, Filter, ArrowUpDown, Tag,
  ChevronLeft, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';
import Layout from '../components/Layout';

const PAGE_SIZE_OPTIONS = [10, 20, 50];
const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'draft', label: 'Draft' },
  { value: 'published', label: 'Published' },
];
const SORT_OPTIONS = [
  { value: 'date', label: 'Date' },
  { value: 'title', label: 'Title (A-Z)' },
  { value: 'category', label: 'Category' },
];

const Articles = () => {
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');   // debounced input
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const navigate = useNavigate();

  // Debounce search input
  useEffect(() => {
    const t = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset page on filter change
  useEffect(() => { setCurrentPage(1); }, [statusFilter, categoryFilter, sortBy, sortOrder, pageSize]);

  const fetchCategories = useCallback(async () => {
    try {
      const res = await api.get('/articles/categories/list');
      setCategories(res.data || []);
    } catch {
      // non-fatal
    }
  }, []);

  const fetchArticles = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pageSize,
        offset: (currentPage - 1) * pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      });
      if (searchQuery) params.set('search', searchQuery);
      if (statusFilter !== 'all') params.set('status', statusFilter);
      if (categoryFilter !== 'all') params.set('category', categoryFilter);

      const res = await api.get(`/articles?${params}`);
      // Handle both old (array) and new (paginated object) response shapes
      if (Array.isArray(res.data)) {
        setArticles(res.data);
        setTotal(res.data.length);
      } else {
        setArticles(res.data.articles || []);
        setTotal(res.data.total || 0);
      }
    } catch {
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, statusFilter, categoryFilter, sortBy, sortOrder, currentPage, pageSize]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);
  useEffect(() => { fetchArticles(); }, [fetchArticles]);

  const handleDelete = async (articleId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;
    try {
      await api.delete(`/articles/${articleId}`);
      toast.success('Article deleted');
      fetchArticles();
    } catch {
      toast.error('Failed to delete article');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  if (loading && articles.length === 0) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Articles</h1>
            <p className="text-slate-500 text-sm mt-1">
              {total > 0 ? `${total} article${total !== 1 ? 's' : ''}` : 'Create and manage blog articles'}
            </p>
          </div>
          <Button onClick={() => navigate('/articles/new')} className="gap-2" data-testid="new-article-btn">
            <Plus size={16} />
            New Article
          </Button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 mb-4">
          <div className="flex flex-wrap gap-3 items-center">
            {/* Search */}
            <div className="relative flex-1 min-w-[180px] max-w-xs">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="pl-9 h-9"
                data-testid="article-search-input"
              />
            </div>

            {/* Category Filter */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger className="w-44 h-9" data-testid="category-filter">
                <Tag size={14} className="mr-1.5 shrink-0" />
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="none">Uncategorized</SelectItem>
                {categories.map(cat => (
                  <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Status Filter */}
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-9" data-testid="status-filter">
                <Filter size={14} className="mr-1.5 shrink-0" />
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                {STATUS_OPTIONS.map(s => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-36 h-9" data-testid="sort-by">
                <ArrowUpDown size={14} className="mr-1.5 shrink-0" />
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent>
                {SORT_OPTIONS.map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="outline"
              size="sm"
              className="h-9"
              onClick={() => setSortOrder(o => o === 'asc' ? 'desc' : 'asc')}
              data-testid="sort-order-toggle"
            >
              {sortOrder === 'asc' ? '↑ Asc' : '↓ Desc'}
            </Button>

            {/* Per-page */}
            <Select value={String(pageSize)} onValueChange={v => setPageSize(Number(v))}>
              <SelectTrigger className="w-24 h-9" data-testid="page-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map(n => (
                  <SelectItem key={n} value={String(n)}>{n} / page</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Articles Table */}
        {articles.length === 0 && !loading ? (
          <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed border-slate-200">
            <FileText className="mx-auto h-12 w-12 text-slate-300 mb-4" />
            <h3 className="text-lg font-medium text-slate-700 mb-2">
              {total === 0 && !searchQuery && statusFilter === 'all' && categoryFilter === 'all'
                ? 'No articles yet'
                : 'No articles match your filters'}
            </h3>
            <p className="text-slate-500 mb-4 text-sm">
              {total === 0 && !searchQuery && statusFilter === 'all' && categoryFilter === 'all'
                ? 'Create your first article to get started'
                : 'Try adjusting your search or filters'}
            </p>
            {total === 0 && !searchQuery && (
              <Button onClick={() => navigate('/articles/new')} variant="outline" className="gap-2">
                <Plus size={16} />
                Create Article
              </Button>
            )}
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{ width: '45%' }} />
                  <col style={{ width: '20%' }} className="hidden md:table-column" />
                  <col style={{ width: '12%' }} className="hidden sm:table-column" />
                  <col style={{ width: '10%' }} />
                  <col style={{ width: '13%' }} />
                </colgroup>
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Article</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600 hidden md:table-cell">Category</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600 hidden sm:table-cell">Date</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-600">Status</th>
                    <th className="text-right px-4 py-3 text-sm font-medium text-slate-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {loading
                    ? Array.from({ length: pageSize }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          <td className="px-4 py-3"><div className="h-4 bg-slate-100 rounded w-3/4" /></td>
                          <td className="px-4 py-3 hidden md:table-cell"><div className="h-4 bg-slate-100 rounded w-1/2" /></td>
                          <td className="px-4 py-3 hidden sm:table-cell"><div className="h-4 bg-slate-100 rounded w-20" /></td>
                          <td className="px-4 py-3"><div className="h-4 bg-slate-100 rounded w-16" /></td>
                          <td className="px-4 py-3"><div className="h-4 bg-slate-100 rounded w-10 ml-auto" /></td>
                        </tr>
                      ))
                    : articles.map((article) => {
                        const cats = [];
                        if (article.category) cats.push(article.category);
                        if (article.categories) cats.push(...article.categories.filter(c => c !== article.category));
                        const visibleCats = cats.slice(0, 2);
                        const extraCount = cats.length - visibleCats.length;

                        return (
                          <tr key={article.article_id} className="hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3 min-w-0">
                                {article.image_url ? (
                                  <img
                                    src={article.image_url}
                                    alt=""
                                    className="w-10 h-10 rounded object-cover object-top flex-shrink-0"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded bg-slate-100 flex items-center justify-center flex-shrink-0">
                                    <ImageIcon size={16} className="text-slate-400" />
                                  </div>
                                )}
                                <div className="min-w-0">
                                  <Link
                                    to={`/articles/${article.article_id}`}
                                    className="font-medium text-slate-800 hover:text-indigo-600 block truncate"
                                    data-testid={`article-title-${article.article_id}`}
                                  >
                                    {article.title}
                                  </Link>
                                  {cats.length > 0 && (
                                    <p className="text-xs text-slate-400 truncate md:hidden mt-0.5">
                                      {cats.slice(0, 2).join(', ')}{cats.length > 2 ? ` +${cats.length - 2}` : ''}
                                    </p>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell">
                              <div className="flex flex-wrap gap-1">
                                {cats.length === 0 ? (
                                  <span className="text-slate-400 text-sm">—</span>
                                ) : (
                                  <>
                                    {visibleCats.map(cat => (
                                      <span key={cat} className="inline-block px-2 py-0.5 bg-indigo-50 text-indigo-700 text-xs rounded-full truncate max-w-[120px]">
                                        {cat}
                                      </span>
                                    ))}
                                    {extraCount > 0 && (
                                      <span className="inline-block px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full">
                                        +{extraCount}
                                      </span>
                                    )}
                                  </>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <div className="flex items-center gap-1 text-sm text-slate-600 whitespace-nowrap">
                                <Calendar size={13} className="text-slate-400 shrink-0" />
                                {formatDate(article.date || article.created_at)}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${
                                article.status === 'published'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-amber-100 text-amber-700'
                              }`}>
                                {article.status === 'published' ? <Eye size={11} /> : <EyeOff size={11} />}
                                {article.status}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => navigate(`/articles/${article.article_id}`)}
                                  className="h-8 w-8 p-0"
                                  data-testid={`edit-article-${article.article_id}`}
                                >
                                  <Edit size={15} />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(article.article_id, article.title)}
                                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-article-${article.article_id}`}
                                >
                                  <Trash2 size={15} />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                  }
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > pageSize && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
                <p className="text-sm text-slate-500">
                  {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, total)} of {total} articles
                </p>
                <div className="flex items-center gap-1">
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft size={15} />
                  </Button>

                  {/* Page number pills */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                    .reduce((acc, p, idx, arr) => {
                      if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((item, idx) =>
                      item === '…' ? (
                        <span key={`ellipsis-${idx}`} className="px-1 text-slate-400 text-sm">…</span>
                      ) : (
                        <Button
                          key={item}
                          variant={item === currentPage ? 'default' : 'outline'}
                          size="sm"
                          className="h-8 w-8 p-0 text-xs"
                          onClick={() => setCurrentPage(item)}
                          data-testid={`pagination-page-${item}`}
                        >
                          {item}
                        </Button>
                      )
                    )
                  }

                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 w-8 p-0"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                    data-testid="pagination-next"
                  >
                    <ChevronRight size={15} />
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Articles;
